import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X, Receipt } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_COLOR = {
  pending: "text-orange-600 bg-orange-50",
  approved: "text-emerald-600 bg-emerald-50",
  rejected: "text-red-600 bg-red-50",
  paid: "text-blue-600 bg-blue-50",
};

export default function Reimbursements() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const { data: rows } = useQuery({
    queryKey: ["reimbursements", status],
    queryFn: async () => (await api.get(`/reimbursements${status ? `?status=${status}` : ""}`)).data,
  });

  const decide = async (id, s) => {
    try {
      await api.put(`/reimbursements/${id}/decide`, { status: s });
      toast.success(`Reimbursement ${s}`);
      qc.invalidateQueries({ queryKey: ["reimbursements"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-head font-extrabold text-slate-900 flex items-center gap-2"><Receipt className="h-7 w-7 text-blue-600" /> Expense Reimbursements</h1>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="reimb-status-filter" className="w-44"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved (unpaid)</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
        Approved reimbursements are added to the employee's payroll as a separate line and are marked <b>paid</b> when that payroll is locked, so they are never added twice.
      </div>

      <div className="lg:hidden space-y-3">
        {(rows || []).length === 0 && <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-400">No reimbursement claims.</div>}
        {(rows || []).map((r) => (
          <div key={r.id} data-testid={`reimb-card-${r.id}`} className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0"><div className="font-semibold text-slate-900 truncate">{r.employee_name}</div><div className="text-xs text-slate-400 mt-0.5">{r.date} · {r.category}</div></div>
              <span className={`shrink-0 text-xs px-2 py-1 rounded-full ${STATUS_COLOR[r.status]}`}>{r.status}</span>
            </div>
            <div className="mt-2 flex items-center justify-between"><span className="text-xs text-slate-400">Amount</span><span className="font-semibold text-slate-900">{money(r.amount)}</span></div>
            {r.description && <div className="mt-1 text-xs text-slate-500">{r.description}</div>}
            {r.status === "pending" && (
              <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
                <button data-testid={`approve-reimb-m-${r.id}`} onClick={() => decide(r.id, "approved")} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm"><Check className="h-4 w-4" /> Approve</button>
                <button data-testid={`reject-reimb-m-${r.id}`} onClick={() => decide(r.id, "rejected")} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-red-50 text-red-600 text-sm"><X className="h-4 w-4" /> Reject</button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="hidden lg:block bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
            <th className="text-left px-4 py-3">Employee</th><th className="text-left px-4 py-3">Date</th>
            <th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Amount</th>
            <th className="text-left px-4 py-3 hidden md:table-cell">Description</th>
            <th className="text-left px-4 py-3">Status</th><th></th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(rows || []).length === 0 && <tr><td colSpan="7" className="px-4 py-6 text-slate-400">No reimbursement claims.</td></tr>}
            {(rows || []).map((r) => (
              <tr key={r.id} data-testid={`reimb-row-${r.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{r.employee_name}</td>
                <td className="px-4 py-3">{r.date}</td>
                <td className="px-4 py-3">{r.category}</td>
                <td className="px-4 py-3 font-semibold">{money(r.amount)}</td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-500">{r.description}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLOR[r.status]}`}>{r.status}</span></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {r.status === "pending" && <>
                    <button data-testid={`approve-reimb-${r.id}`} onClick={() => decide(r.id, "approved")} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"><Check className="h-4 w-4" /></button>
                    <button data-testid={`reject-reimb-${r.id}`} onClick={() => decide(r.id, "rejected")} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><X className="h-4 w-4" /></button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
